import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type MisuCancelRow } from '../lib/api';
import { DataTable, ErrorBox, dash, useList, type Column, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { CancelRequestDetail, CancelStatusChip, KindChip, amountLine } from './InvoiceCancelRequests';

/**
 * 미수금 › 「취소·수정」 — 청구 취소·수정 요청 **결과 조회만**(2026-10-19 way 「미수금 관리에서 … 취소 요청 승인 이런 절차는 필요 없네 ·
 * 미수는 결과만 나타내면」). 승인·반려는 **회계 › 발행 요청** 의 「취소」·「수정」 칩에서 한다(서버 `misuCancel.ts`).
 * 위칸은 미수·미청구와 같은 한 줄(보기 전환 + 「처리된 것도」). 대기 줄을 누르면 처리 화면으로 안내한다.
 */
export default function SettleMisuCancel({ toggle }: { toggle: React.ReactNode; onChanged?: () => void }) {
  const { me } = useMe();
  const canDecide = canScreen(me, 'accounting', 'write');
  const navigate = useNavigate();
  const [all, setAll] = useState(true);
  const [open, setOpen] = useState<MisuCancelRow | null>(null);

  const { rows, loading, error } = useList(
    async () => {
      const r = await api.listMisuCancelRequests({ status: all ? '전체' : '대기' });
      return { rows: r.rows as unknown as Row[] };
    },
    [all],
  );

  const at = (v: unknown) => String(v ?? '').slice(0, 16).replace('T', ' ');
  const columns: Column[] = [
    { key: 'kind', label: '구분', render: (r) => <KindChip kind={String(r.kind)} /> },
    { key: 'status', label: '결과', render: (r) => <CancelStatusChip s={String(r.status)} kind={String(r.kind)} /> },
    { key: 'cust_name', label: '거래처', phone: 'pin', render: (r) => <span>{dash(r.cust_name)} <span className="text-xs text-ink-faint">#{dash(r.recv_id)}</span></span> },
    { key: 'drive_mon', label: '정산월' },
    { key: 'old_total', label: '금액', align: 'right', render: (r) => <span className="whitespace-nowrap">{amountLine(r as unknown as MisuCancelRow)}</span> },
    {
      key: 'cur_req_status',
      label: '지금 상태',
      render: (r) => <span className="whitespace-nowrap text-xs">{dash(r.cur_req_status)}{r.cur_bank_status ? ` · ${String(r.cur_bank_status)}` : ''}</span>,
    },
    { key: 'requested_name', label: '요청자', render: (r) => dash(r.requested_name ?? r.requested_by) },
    { key: 'reason', label: '사유', render: (r) => <span className="break-keep">{dash(r.reason)}{r.fix_note ? <span className="block text-xs text-violet-800">수정: {String(r.fix_note)}</span> : null}</span> },
    { key: 'requested_at', label: '요청시각', phone: 'hide', render: (r) => <span className="whitespace-nowrap text-xs">{at(r.requested_at)}</span> },
    {
      key: 'decided_at',
      label: '처리',
      phone: 'hide',
      render: (r) => r.decided_at
        ? <span className="text-xs text-ink-soft" title={String(r.decide_note ?? '')}>{dash(r.decided_name ?? r.decided_by)} · {at(r.decided_at)}{r.decide_note ? ` · ${String(r.decide_note)}` : ''}</span>
        : <span className="text-xs text-amber-800">{r.seen_name ? `${String(r.seen_name)} 확인 중` : '회계 확인 전'}</span>,
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {toggle}
        <label className="flex items-center gap-1 whitespace-nowrap text-xs text-ink-soft" title="끄면 처리 대기만(최근 300건)">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          처리된 것도
        </label>
        <span className="whitespace-nowrap text-xs text-ink-faint">결과 조회 — 승인·반려는 회계 › 발행 요청 「취소」·「수정」에서</span>
        {canDecide && (
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => navigate('/accounting?tab=invreq')}>발행 요청 화면으로</button>
        )}
      </div>
      {error && <ErrorBox message={error} />}
      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        tableId="settle.misuCancel"
        onRowClick={(r) => setOpen(r as unknown as MisuCancelRow)}
        activeRow={(r) => open?.request_id === r.request_id}
      />
      {!loading && rows.length === 0 && !error && (
        <p className="text-sm text-ink-faint">{all ? '취소·수정 요청이 없습니다.' : '처리를 기다리는 요청이 없습니다.'}</p>
      )}
      {open && (
        <Modal width="max-w-2xl">
          <CancelRequestDetail id={Number(open.request_id)} initial={open} readOnly onClose={() => setOpen(null)} />
        </Modal>
      )}
    </div>
  );
}
